<template>
  <AppLayout>
    <template #header>
      <PageHeadingWithActionsAndBreadcrumbs
        :title="`Pilots on ${props.plane.tail_number}`"
        :breadcrumbs="breadcrumbs"
      >
        <template #actions>
          <ButtonBase
            :leadingIcon="PlusSmIcon"
            @click="showingInviteModal = true"
            >Invite Pilot</ButtonBase
          >
        </template>
      </PageHeadingWithActionsAndBreadcrumbs>
    </template>

    <TableBase>
      <template #before>
        <CardHeadingWithDescriptionAction
          title="Pilots"
          description="Pilots can be assigned to fly trips as PIC or SIC. Invited pilots get an email to set up their own login and then see the flights they're on."
        />
      </template>

      <template v-if="props.pilots.length" #tbody>
        <tr v-for="pilot in props.pilots" :key="pilot.id">
          <TableCellBase bold>
            <div class="flex items-center">
              <AvatarUser :user="pilot" size="sm" />
              <span class="ml-3">{{ pilot.name }}</span>
              <span
                class="ml-3 rounded px-1 text-xs font-semibold leading-4"
                :class="pilotChipClasses(pilot.calendar_color)"
                :title="`${pilot.name}'s color on the calendar`"
                >{{ initials(pilot.name) }}</span
              >
            </div>
          </TableCellBase>
          <TableCellBase>{{ pilot.email || 'No email' }}</TableCellBase>
          <TableCellBase>
            <BadgeBase v-if="pilot.is_default" color="indigo"
              >Default pilot</BadgeBase
            >
            <BadgeBase v-if="!pilot.has_login" color="yellow" class="ml-2"
              >Invited</BadgeBase
            >
          </TableCellBase>
          <TableCellBase right>
            <LinkBase
              v-if="!pilot.has_login && pilot.email"
              class="mr-4"
              @click="resend(pilot)"
              >Resend invite</LinkBase
            >
            <LinkBase v-if="!pilot.is_default" @click="removing = pilot"
              >Remove</LinkBase
            >
          </TableCellBase>
        </tr>
      </template>

      <template v-else #after>
        <CardPadding>
          <ParagraphText color="text-gray-500"
            >No pilots yet. Invite one to get started.</ParagraphText
          >
        </CardPadding>
      </template>
    </TableBase>

    <ModalSimpleAlertWithDismiss
      @confirm="remove"
      @close="removing = null"
      :show="!!removing"
      :processing="removeProcessing"
      title="Remove pilot"
      :description="removeDescription"
      confirmLabel="Remove"
    />

    <InvitePilotModal
      :show="showingInviteModal"
      @close="showingInviteModal = false"
      :plane="props.plane"
    />
  </AppLayout>
</template>

<script setup>
import AppLayout from '@/Layouts/AppLayout.vue'
import PageHeadingWithActionsAndBreadcrumbs from '@/Common/Headings/PageHeadings/PageHeadingWithActionsAndBreadcrumbs.vue'
import ButtonBase from '@/Common/Elements/Buttons/ButtonBase.vue'
import TableBase from '@/Common/Lists/Tables/TableBase.vue'
import TableCellBase from '@/Common/Lists/Tables/TableCellBase.vue'
import CardHeadingWithDescriptionAction from '@/Common/Headings/CardHeadings/CardHeadingWithDescriptionAction.vue'
import CardPadding from '@/Common/Layout/Containers/CardPadding.vue'
import ParagraphText from '@/Common/Text/ParagraphText.vue'
import AvatarUser from '@/Common/Elements/Avatars/AvatarUser.vue'
import BadgeBase from '@/Common/Elements/Badges/BadgeBase.vue'
import LinkBase from '@/Common/Text/LinkBase.vue'
import ModalSimpleAlertWithDismiss from '@/Common/Overlays/Modals/ModalSimpleAlertWithDismiss.vue'
import InvitePilotModal from './InvitePilotModal.vue'
import { PlusSmIcon } from '@heroicons/vue/solid'
import { initials, pilotChipClasses } from '../../Calendar/pilotColors'
import { Inertia } from '@inertiajs/inertia'
import { computed, ref } from 'vue'

const props = defineProps({
  plane: Object,
  pilots: Array,
})

const breadcrumbs = [
  {
    title: props.plane.tail_number,
    href: route('planes.show', props.plane),
  },
]

// Invite
const showingInviteModal = ref(false)

function resend(pilot) {
  Inertia.post(
    route('planes.pilots.invite', {
      plane: props.plane.tail_number,
      user: pilot.id,
    }),
    {},
    { preserveScroll: true }
  )
}

// Remove
const removing = ref(null)
const removeProcessing = ref(false)

const removeDescription = computed(() =>
  removing.value
    ? `Remove ${removing.value.name} as a pilot of ${props.plane.tail_number}? Trips they are already on are not changed.`
    : ''
)

function remove() {
  Inertia.delete(
    route('planes.pilots.destroy', {
      plane: props.plane.tail_number,
      user: removing.value.id,
    }),
    {
      preserveScroll: true,
      onStart: () => (removeProcessing.value = true),
      onFinish: () => {
        removeProcessing.value = false
        removing.value = null
      },
    }
  )
}
</script>
